import type { ActivateReceiptPostingRequest } from '@pokerroom/types';

const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
export const postingRecoveryKey = (actor: string, venue: string) =>
  `venue-receipt-posting:${actor}:${venue}`;

export function readPostingAttempt(actor: string | undefined, venue: string) {
  try {
    if (!actor) return { request: null, unreadable: false };
    const raw = sessionStorage.getItem(postingRecoveryKey(actor, venue));
    if (raw === null) return { request: null, unreadable: false };
    const row = JSON.parse(raw);
    const request = row.request;
    if (
      row.version !== 1 ||
      row.actor !== actor ||
      row.venue !== venue ||
      !request ||
      Object.keys(request).sort().join(',') !== 'confirmed,operationKey,previewHash,reason' ||
      !uuid.test(request.operationKey) ||
      !/^[a-f0-9]{64}$/.test(request.previewHash) ||
      request.confirmed !== true ||
      typeof request.reason !== 'string' ||
      !request.reason.trim() ||
      request.reason.length > 500
    )
      throw new Error('Invalid recovery');
    return { request: request as ActivateReceiptPostingRequest, unreadable: false };
  } catch {
    return { request: null, unreadable: true };
  }
}

export function savePostingAttempt(
  actor: string | undefined,
  venue: string,
  request: ActivateReceiptPostingRequest,
  requireExisting = false,
  tag: string = crypto.randomUUID(),
) {
  if (!actor) throw new Error('POSTING_RECOVERY_UNAVAILABLE');
  try {
    const stored = readPostingAttempt(actor, venue);
    if (
      stored.unreadable ||
      (requireExisting && !stored.request) ||
      (stored.request &&
        (stored.request.operationKey !== request.operationKey ||
          stored.request.previewHash !== request.previewHash ||
          stored.request.reason !== request.reason ||
          stored.request.confirmed !== request.confirmed))
    )
      throw new Error('Original operation changed');
    const key = postingRecoveryKey(actor, venue);
    const value = JSON.stringify({ version: 1, actor, venue, request, tag });
    sessionStorage.setItem(key, value);
    if (sessionStorage.getItem(key) !== value) throw new Error('Write not retained');
    return tag;
  } catch {
    throw new Error('POSTING_RECOVERY_UNAVAILABLE');
  }
}

export function clearPostingAttempt(actor: string | undefined, venue: string, tag?: string) {
  try {
    if (!actor || !tag) return;
    const key = postingRecoveryKey(actor, venue);
    const raw = sessionStorage.getItem(key);
    if (raw !== null) {
      const row = JSON.parse(raw);
      if (row.actor === actor && row.venue === venue && row.tag === tag)
        sessionStorage.removeItem(key);
    }
  } catch {
    /* A retained original operation is safe to replay after reauthorization. */
  }
}
